import { request } from '../request';

/**
 * 查询我的项目渠道运营数据
 * GET /my/projects/{id}/channels/{channel_id}/metrics
 *
 * @param id 发行项目 ID
 * @param channelId 项目渠道 ID
 * @param params 查询参数 (start_date, end_date, region_code, episode_no)
 * @returns 渠道运营指标数据列表
 */
export function fetchProjectChannelMetrics(
  id: number | string,
  channelId: number | string,
  params?: Api.Revenue.ProjectChannelMetricsParams
) {
  return request<Api.Revenue.MetricResponse[]>({
    url: `/my/projects/${id}/channels/${channelId}/metrics`,
    method: 'GET',
    params
  });
}

/**
 * 获取我的发行项目收益看板
 * GET /my/projects/{id}/revenue-dashboard
 *
 * 返回当前内容方有权查看的项目累计运营数据、按结算币种汇总的收入/分润/结算金额，以及账单和结算状态数量。
 *
 * @param id 发行项目 ID
 * @returns 发行项目收益看板响应数据
 */
export function fetchProjectRevenueDashboard(id: number | string) {
  return request<Api.Revenue.RevenueDashboardResponse>({
    url: `/my/projects/${id}/revenue-dashboard`,
    method: 'GET'
  });
}

/**
 * 查询我的项目收益账单
 * GET /my/projects/{id}/statements
 *
 * @param id 发行项目 ID
 * @param params 查询参数 (project_channel_id, status, start_date, end_date, page, page_size)
 * @returns 项目收益账单分页数据
 */
export function fetchProjectStatements(
  id: number | string,
  params?: Api.Revenue.ProjectStatementsParams
) {
  return request<Api.Revenue.StatementPageResponse>({
    url: `/my/projects/${id}/statements`,
    method: 'GET',
    params
  });
}

/**
 * 查询我的结算申请列表
 * GET /my/settlements
 *
 * 列表项的 id 可用于调用 GET /my/settlements/{id}，查询项目/渠道结算条目、付款记录和结算资料快照。
 *
 * @param params 查询参数 (project_id, status, statuses, currency, start_date, end_date, page, page_size)
 * @returns 结算申请列表分页响应数据
 */
export function fetchSettlementRequests(params?: Api.Revenue.SettlementQueryParams) {
  return request<Api.Revenue.SettlementPageResponse>({
    url: '/my/settlements',
    method: 'GET',
    params
  });
}

/**
 * 查询我的结算申请详情和付款记录
 * GET /my/settlements/{id}
 *
 * @param id 结算申请 ID
 * @returns 结算申请详情和打款记录
 */
export function fetchSettlementDetail(id: number | string) {
  return request<Api.Revenue.SettlementDetailResponse>({
    url: `/my/settlements/${id}`,
    method: 'GET'
  });
}

/**
 * 提交内容方汇总结算单异议
 * POST /my/settlements/{id}/dispute
 *
 * @param id 结算单 ID
 * @param data 请求体参数 (note)
 * @returns 更新后的结算单详情
 */
export function fetchDisputeSettlement(
  id: number | string,
  data: Api.Revenue.SettlementDisputeRequest
) {
  return request<Api.Revenue.SettlementDetailResponse>({
    url: `/my/settlements/${id}/dispute`,
    method: 'POST',
    data
  });
}

/**
 * 确认汇总结算单并提交结算申请（需结算资料审核通过）
 * POST /my/settlements/{id}/confirm
 *
 * @param id 结算单 ID
 * @returns 确认后的结算单详情
 */
export function fetchConfirmSettlement(id: number | string) {
  return request<Api.Revenue.SettlementDetailResponse>({
    url: `/my/settlements/${id}/confirm`,
    method: 'POST'
  });
}

/**
 * 查询我的结算申请审核记录
 * GET /my/settlements/{id}/review-records
 *
 * @param id 结算申请 ID
 * @returns 结算申请审核记录列表
 */
export function fetchSettlementReviewRecords(id: number | string) {
  return request<Api.Revenue.SettlementReviewRecordResponse[]>({
    url: `/my/settlements/${id}/review-records`,
    method: 'GET'
  });
}

/**
 * 查询我的收益账单详情
 * GET /my/statements/{id}
 *
 * @param id 收益账单 ID
 * @returns 收益账单明细数据
 */
export function fetchStatementDetail(id: number | string) {
  return request<Api.Revenue.StatementDetailResponse>({
    url: `/my/statements/${id}`,
    method: 'GET'
  });
}

/**
 * 确认收益账单
 * POST /my/statements/{id}/confirm
 *
 * @param id 收益账单 ID
 * @returns 确认后的收益账单详情
 */
export function fetchConfirmStatement(id: number | string) {
  return request<Api.Revenue.StatementDetailResponse>({
    url: `/my/statements/${id}/confirm`,
    method: 'POST'
  });
}

/**
 * 提交收益账单异议
 * POST /my/statements/{id}/dispute
 *
 * @param id 收益账单 ID
 * @param data 请求体参数 (attachments, note)
 * @returns 提交异议后的收益账单详情
 */
export function fetchDisputeStatement(
  id: number | string,
  data: Api.Revenue.DisputeRequest
) {
  return request<Api.Revenue.StatementDetailResponse>({
    url: `/my/statements/${id}/dispute`,
    method: 'POST',
    data
  });
}

/**
 * 查询我的收益账单异议处理记录
 * GET /my/statements/{id}/dispute-records
 *
 * @param id 收益账单 ID
 * @returns 收益账单异议处理记录列表
 */
export function fetchStatementDisputeRecords(id: number | string) {
  return request<Api.Revenue.StatementDisputeRecordResponse[]>({
    url: `/my/statements/${id}/dispute-records`,
    method: 'GET'
  });
}

/**
 * 基于已确认账单申请结算
 * POST /my/statements/{id}/settle
 *
 * @param id 收益账单 ID
 * @param data 请求体参数 (applicant_note)
 * @returns 结算申请响应
 */
export function fetchApplySettlement(
  id: number | string,
  data?: Api.Revenue.SettlementApplyRequest
) {
  return request<Api.Revenue.SettlementResponse>({
    url: `/my/statements/${id}/settle`,
    method: 'POST',
    data
  });
}

/**
 * 获取我的企业结算资料
 * GET /my/settlement-profile/enterprise
 *
 * @returns 企业结算资料详情
 */
export function fetchEnterpriseSettlementProfile() {
  return request<Api.Revenue.ProfileResponse>({
    url: '/my/settlement-profile/enterprise',
    method: 'GET'
  });
}

/**
 * 提交或更新企业结算资料
 * POST /my/settlement-profile/enterprise
 *
 * 结算主体名称由已通过的企业认证自动写入；非账号或企业认证手机号必须提供通过 /verification-challenges 获取的对应验证 token。
 *
 * @param data 企业结算资料请求参数
 * @returns 更新后的结算资料响应
 */
export function fetchUpdateEnterpriseSettlementProfile(
  data: Api.Revenue.EnterpriseSettlementProfileRequest
) {
  return request<Api.Revenue.ProfileResponse>({
    url: '/my/settlement-profile/enterprise',
    method: 'POST',
    data
  });
}

/**
 * 查询我的企业结算资料审核记录
 * GET /my/settlement-profile/enterprise/review-records
 *
 * @returns 企业结算资料审核记录列表
 */
export function fetchEnterpriseSettlementProfileReviewRecords() {
  return request<Api.Revenue.ProfileReviewRecordResponse[]>({
    url: '/my/settlement-profile/enterprise/review-records',
    method: 'GET'
  });
}

/**
 * 获取我的个人结算资料
 * GET /my/settlement-profile/personal
 *
 * @returns 个人结算资料详情
 */
export function fetchPersonalSettlementProfile() {
  return request<Api.Revenue.ProfileResponse>({
    url: '/my/settlement-profile/personal',
    method: 'GET'
  });
}

/**
 * 提交或更新个人结算资料
 * POST /my/settlement-profile/personal
 *
 * @param data 个人结算资料请求参数
 * @returns 更新后的结算资料响应
 */
export function fetchUpdatePersonalSettlementProfile(
  data: Api.Revenue.PersonalSettlementProfileRequest
) {
  return request<Api.Revenue.ProfileResponse>({
    url: '/my/settlement-profile/personal',
    method: 'POST',
    data
  });
}

/**
 * 查询我的个人结算资料审核记录
 * GET /my/settlement-profile/personal/review-records
 *
 * @returns 个人结算资料审核记录列表
 */
export function fetchPersonalSettlementProfileReviewRecords() {
  return request<Api.Revenue.ProfileReviewRecordResponse[]>({
    url: '/my/settlement-profile/personal/review-records',
    method: 'GET'
  });
}

/**
 * 查询我的项目已审核收入账期
 * GET /my/projects/{id}/revenue-periods
 *
 * @param id 发行项目 ID
 * @param params 查询参数 (project_channel_id, start_date, end_date, page, page_size)
 * @returns 项目已审核收入账期分页响应数据
 */
export function fetchProjectRevenuePeriods(
  id: number | string,
  params?: Api.Revenue.ProjectRevenuePeriodsParams
) {
  return request<Api.Revenue.PeriodPageResponse>({
    url: `/my/projects/${id}/revenue-periods`,
    method: 'GET',
    params
  });
}

/**
 * 查询我全部发行项目的每日收益
 * GET /my/revenue-periods
 *
 * 仅返回当前用户项目中已审核的日收入记录；可按发行项目、项目渠道和收益日期筛选。
 *
 * @param params 查询参数 (project_id, project_channel_id, start_date, end_date, page, page_size)
 * @returns 全部发行项目每日收益分页响应数据
 */
export function fetchMyRevenuePeriods(params?: Api.Revenue.MyRevenuePeriodsParams) {
  return request<Api.Revenue.MyRevenuePeriodPageResponse>({
    url: '/my/revenue-periods',
    method: 'GET',
    params
  });
}

/**
 * @deprecated 请使用 fetchEnterpriseSettlementProfile 或 fetchPersonalSettlementProfile
 * 获取我的结算资料
 * GET /my/settlement-profile
 */
export function fetchSettlementProfile() {
  return request<Api.Revenue.ProfileResponse>({
    url: '/my/settlement-profile',
    method: 'GET'
  });
}

/**
 * @deprecated 请使用 fetchUpdateEnterpriseSettlementProfile 或 fetchUpdatePersonalSettlementProfile
 * 提交或更新我的结算资料
 * POST /my/settlement-profile
 *
 * @param data 请求体参数 (enterprise, personal)
 */
export function fetchUpdateSettlementProfile(data: Api.Revenue.ProfileRequest) {
  return request<Api.Revenue.ProfileResponse>({
    url: '/my/settlement-profile',
    method: 'POST',
    data
  });
}

/**
 * @deprecated 请使用 fetchEnterpriseSettlementProfileReviewRecords 或 fetchPersonalSettlementProfileReviewRecords
 * 查询我的结算资料审核记录
 * GET /my/settlement-profile/review-records
 */
export function fetchSettlementProfileReviewRecords() {
  return request<Api.Revenue.ProfileReviewRecordResponse[]>({
    url: '/my/settlement-profile/review-records',
    method: 'GET'
  });
}
